'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Search, Menu, X, Plus, ChevronDown, LogOut, LayoutDashboard, Store, PawPrint, User, Search as SearchIcon, Heart, ShoppingBag, Wallet, MessageCircle, Bell } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Image from 'next/image';

const navLinks = [
  { label: 'Home', href: '/homepage' },
  { label: 'Browse Listings', href: '/listings-feed' },
  { label: 'Wanted', href: '/wanted' },
  { label: 'Breeders', href: '/breeders' },
  { label: 'Resources', href: '/resources' },
];

type Notification = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  is_read: boolean;
  created_at: string;
};

export default function Topbar() {
  const pathname = usePathname();
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const notifRef = useRef<HTMLDivElement>(null);
  const { user, signOut, loading } = useAuth();

  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    fetchUnreadCount();
    fetchNotifications();
    const supabase = createClient();
    const channel = supabase
      .channel('topbar-unread')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'conversations',
        filter: `buyer_id=eq.${user.id}`,
      }, () => fetchUnreadCount())
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'conversations',
        filter: `seller_id=eq.${user.id}`,
      }, () => fetchUnreadCount())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` }, () => fetchNotifications())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  async function fetchUnreadCount() {
    if (!user) return;
    const supabase = createClient();
    const { data } = await supabase
      .from('conversations')
      .select('buyer_id, seller_id, buyer_unread, seller_unread')
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`);
    if (data) {
      const total = (data as any[]).reduce((sum, c) => {
        const unread = c.buyer_id === user.id ? (c.buyer_unread || 0) : (c.seller_unread || 0);
        return sum + unread;
      }, 0);
      setUnreadCount(total);
    }
  }

  async function fetchNotifications() {
    if (!user) return;
    const supabase = createClient();
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);
    const notifs = (data as Notification[]) || [];
    setNotifications(notifs);
    setUnreadNotifCount(notifs.filter((n) => !n.is_read).length);
  }

  async function markAllRead() {
    if (!user) return;
    const supabase = createClient();
    await supabase.from('notifications').update({ is_read: true }).eq('user_id', user.id).eq('is_read', false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadNotifCount(0);
  }

  async function markOneRead(id: string) {
    const supabase = createClient();
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
    setUnreadNotifCount((c) => Math.max(0, c - 1));
  }

  async function handleSignOut() {
    try {
      await signOut();
      router.push('/homepage');
      router.refresh();
    } catch {}
  }

  function handleSearch(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && searchValue.trim()) {
      router.push(`/listings-feed?q=${encodeURIComponent(searchValue.trim())}`);
    }
  }

  const userInitials = user?.user_metadata?.full_name
    ? user.user_metadata.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() ?? 'DM';

  const totalBadge = unreadNotifCount;

  function timeAgo(dateStr: string) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-[hsl(222,25%,18%)] bg-[hsl(222,47%,6%)]/95 backdrop-blur-md">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          <div className="flex items-center h-16 gap-4">
            {/* Brand */}
            <Link href="/homepage" className="flex items-center shrink-0 group gap-2">
              <Image
                src="/assets/images/Untitled-1774970632290.jpeg"
                alt="Driven Dog Training logo"
                width={48}
                height={48}
                className="rounded-sm object-contain"
                priority
              />
              <span className="font-logo text-xl tracking-tight text-white">
                Driven<span className="text-[hsl(28,85%,48%)]">Market</span>
              </span>
            </Link>

            {/* Search */}
            <div className="flex-1 max-w-xl mx-4 hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e?.target?.value)}
                  onKeyDown={handleSearch}
                  placeholder="Search bite sleeves, crates, e-collars..."
                  className="w-full bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-[hsl(28,85%,48%)]/60 focus:ring-1 focus:ring-[hsl(28,85%,48%)]/30 transition-all duration-150"
                />
              </div>
            </div>

            {/* Nav links */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks?.map((link) => (
                <Link
                  key={`nav-${link?.label?.toLowerCase().replace(/\s+/g, '-')}`}
                  href={link?.href}
                  className={`relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    pathname === link?.href
                      ? 'bg-[hsl(28,85%,48%)]/10 text-[hsl(28,85%,48%)]'
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link?.label}
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2 ml-auto lg:ml-0">
              <Link
                href="/create-listing"
                className="hidden sm:flex items-center gap-1.5 bg-[hsl(28,85%,48%)] hover:bg-[hsl(28,85%,41%)] text-white font-semibold px-4 py-2 rounded-lg text-sm transition-all duration-150 active:scale-95"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden md:block">Sell</span>
              </Link>

              {/* Messages */}
              {!loading && user && (
                <Link
                  href="/messages"
                  className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-150 ${
                    pathname === '/messages' ? 'bg-[hsl(28,85%,48%)]/10 text-[hsl(28,85%,48%)]' : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                  aria-label="Messages"
                >
                  <MessageCircle className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-amber-500 text-slate-900 text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 border border-[hsl(222,47%,6%)]">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Notification Bell */}
              {!loading && user && (
                <div className="relative" ref={notifRef}>
                  <button
                    onClick={() => setNotifOpen(!notifOpen)}
                    className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-150 ${
                      notifOpen ? 'bg-[hsl(28,85%,48%)]/10 text-[hsl(28,85%,48%)]' : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`}
                    aria-label="Notifications"
                  >
                    <Bell className="w-5 h-5" />
                    {totalBadge > 0 && (
                      <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 border border-[hsl(222,47%,6%)]">
                        {totalBadge > 9 ? '9+' : totalBadge}
                      </span>
                    )}
                  </button>

                  {notifOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-80 bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(222,25%,18%)]">
                          <span className="text-sm font-semibold text-white">Notifications</span>
                          {unreadNotifCount > 0 && (
                            <button onClick={markAllRead} className="text-xs text-amber-400 hover:text-amber-300 transition-colors">
                              Mark all read
                            </button>
                          )}
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                              <Bell className="w-8 h-8 text-slate-600 mb-2" />
                              <p className="text-sm text-slate-500">No notifications yet</p>
                            </div>
                          ) : (
                            notifications.map((n) => (
                              <div
                                key={n.id}
                                onClick={() => {
                                  if (!n.is_read) markOneRead(n.id);
                                  if (n.link) router.push(n.link);
                                  setNotifOpen(false);
                                }}
                                className={`flex items-start gap-3 px-4 py-3 border-b border-[hsl(222,25%,18%)] last:border-0 cursor-pointer transition-colors hover:bg-white/3 ${!n.is_read ? 'bg-amber-500/3' : ''}`}
                              >
                                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.is_read ? 'bg-amber-400' : 'bg-transparent'}`} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-white leading-snug">{n.title}</p>
                                  {n.body && <p className="text-xs text-slate-400 mt-0.5 leading-snug line-clamp-2">{n.body}</p>}
                                  <p className="text-[10px] text-slate-600 mt-1">{timeAgo(n.created_at)}</p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Auth state */}
              {!loading && (
                user ? (
                  <div className="relative">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-white/5 transition-all duration-150"
                    >
                      <div className="w-7 h-7 rounded-full bg-[hsl(28,85%,48%)]/20 border border-[hsl(28,85%,48%)]/30 flex items-center justify-center text-[hsl(28,85%,48%)] text-xs font-bold">
                        {userInitials}
                      </div>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-500 hidden sm:block" />
                    </button>

                    {userMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                        <div className="absolute right-0 top-full mt-2 w-52 bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden">
                          <div className="px-4 py-3 border-b border-[hsl(222,25%,18%)]">
                            <p className="text-xs text-slate-500 truncate">{user.email}</p>
                          </div>
                          <div className="py-1">
                            <Link href="/dashboard" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                              <LayoutDashboard className="w-4 h-4 text-slate-500" />
                              My Dashboard
                            </Link>
                            <Link href="/messages" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                              <MessageCircle className="w-4 h-4 text-slate-500" />
                              <span className="flex-1">Messages</span>
                              {unreadCount > 0 && (
                                <span className="min-w-[18px] h-[18px] bg-amber-500 text-slate-900 text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                                  {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                              )}
                            </Link>
                            <Link href="/order-history" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                              <ShoppingBag className="w-4 h-4 text-slate-500" />
                              Order History
                            </Link>
                            <Link href="/payouts" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                              <Wallet className="w-4 h-4 text-slate-500" />
                              Payouts
                            </Link>
                            <Link href="/saved-listings" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                              <Heart className="w-4 h-4 text-slate-500" />
                              Saved Listings
                            </Link>
                            <Link href="/account" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                              <User className="w-4 h-4 text-slate-500" />
                              Account
                            </Link>
                            <Link href={`/store/${user.id}`} onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                              <Store className="w-4 h-4 text-slate-500" />
                              My Store
                            </Link>
                            <Link href="/wanted/my-posts" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                              <SearchIcon className="w-4 h-4 text-slate-500" />
                              My Wanted Posts
                            </Link>
                            <Link href="/breeders/create" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                              <PawPrint className="w-4 h-4 text-slate-500" />
                              Post Breeder Listing
                            </Link>
                          </div>
                          <div className="border-t border-[hsl(222,25%,18%)] py-1">
                            <button onClick={handleSignOut} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/5 transition-colors">
                              <LogOut className="w-4 h-4" />
                              Sign Out
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Link href="/login" className="text-sm text-slate-300 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 transition-all duration-150">
                      Sign In
                    </Link>
                    <Link href="/register" className="hidden sm:flex items-center gap-1.5 border border-[hsl(222,25%,18%)] hover:border-slate-500 text-slate-300 hover:text-white px-3 py-2 rounded-lg text-sm transition-all duration-150">
                      Register
                    </Link>
                  </div>
                )
              )}

              {/* Mobile menu toggle */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all duration-150"
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile search */}
          <div className="md:hidden pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search equipment..."
                onKeyDown={handleSearch}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-[hsl(28,85%,48%)]/60 transition-all duration-150"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        <div className={`absolute top-16 left-0 right-0 bg-[hsl(222,40%,9%)] border-b border-[hsl(222,25%,18%)] transition-transform duration-300 ${mobileOpen ? 'translate-y-0' : '-translate-y-4'}`}>
          <nav className="px-4 py-4 flex flex-col gap-1">
            {navLinks?.map((link) => (
              <Link
                key={`mobile-nav-${link?.label?.toLowerCase().replace(/\s+/g, '-')}`}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium transition-all duration-150 ${
                  pathname === link?.href
                    ? 'bg-[hsl(28,85%,48%)]/10 text-[hsl(28,85%,48%)]'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link?.label}
              </Link>
            ))}
            <div className="mt-2 pt-3 border-t border-[hsl(222,25%,18%)] flex flex-col gap-2">
              {user ? (
                <>
                  <Link href="/messages" onClick={() => setMobileOpen(false)} className="flex items-center justify-between border border-[hsl(222,25%,18%)] text-slate-300 hover:text-white px-4 py-2.5 rounded-lg text-sm transition-all duration-150 w-full">
                    <span>Messages</span>
                    {unreadCount > 0 && (
                      <span className="min-w-[18px] h-[18px] bg-amber-500 text-slate-900 text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  <Link href="/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 border border-[hsl(222,25%,18%)] text-slate-300 hover:text-white px-4 py-2.5 rounded-lg text-sm transition-all duration-150 w-full">
                    My Dashboard
                  </Link>
                  <Link href="/account" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 border border-[hsl(222,25%,18%)] text-slate-300 hover:text-white px-4 py-2.5 rounded-lg text-sm transition-all duration-150 w-full">
                    Account
                  </Link>
                  <button onClick={() => { handleSignOut(); setMobileOpen(false); }} className="flex items-center justify-center gap-2 text-red-400 px-4 py-2.5 rounded-lg text-sm transition-all duration-150 w-full">
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link href="/login" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 border border-[hsl(222,25%,18%)] text-slate-300 hover:text-white px-4 py-2.5 rounded-lg text-sm transition-all duration-150 w-full">
                    Sign In
                  </Link>
                  <Link href="/create-listing" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 bg-[hsl(28,85%,48%)] hover:bg-[hsl(28,85%,41%)] text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition-all duration-150 w-full">
                    <Plus className="w-4 h-4" />
                    Post a Listing
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}